'use client';
import { useEffect, useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { HoldingsResearchPanel } from '@/components/research/HoldingsResearchPanel';
import { EpsRevisionChart } from '@/components/research/EpsRevisionChart';
import {
  type StoryResp,
  TONE_STYLE,
  VERDICT_TONE,
  SENTIMENT_DOT,
  fmtPct,
  fmtMoney,
  relTime,
} from '@/components/stock-story/types';

export function StockStoryDrawer({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const { data, loading, error } = useResource<StoryResp>(`/api/stock-story/${symbol}`, 60_000);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [fullTranscriptOpen, setFullTranscriptOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (fullTranscriptOpen) { setFullTranscriptOpen(false); return; }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, fullTranscriptOpen]);

  return (
    <div
      className="fixed inset-y-4 right-4 left-[45%] z-50 bg-[#0a0e14] border border-[#1e293b] rounded-lg shadow-2xl overflow-y-auto pt-8"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="contents">
        <div className="sticky top-0 z-10 bg-[#0a0e14] border-b border-[#1e293b] px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-[18px] font-bold tracking-tight text-white">
              {symbol} <span className="text-[#64748b] text-[12px] font-normal">— Why?</span>
            </h2>
            {data && (
              <div className="text-[10px] text-[#64748b] font-mono tabular-nums">
                {data.summary.headline}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-[#94a3b8] hover:text-white text-[20px] leading-none px-2"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {loading && <div className="p-6 text-[#94a3b8] text-sm">Loading story…</div>}
        {error && <div className="p-6 text-red-300 text-sm">Error loading story</div>}

        {data && (
          <div className="p-5 space-y-5">
            {/* Smart Entry verdict — biggest signal. Two-column compact layout:
                left = verdict + prices, right = reasons. Tighter vertical
                footprint per Steve 2026-05-22. */}
            {(() => {
              const tone = VERDICT_TONE[data.smartEntry.verdict];
              return (
                <div className={`border ${tone.border} ${tone.bg} rounded-lg p-3`}>
                  <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-[20px] font-bold tracking-tight ${tone.text}`}>{data.smartEntry.verdict}</span>
                        <span className={`text-[9px] font-semibold uppercase tracking-wider ${tone.text}/80`}>Smart Entry</span>
                      </div>
                      <div className="mt-1 text-[11px] text-white space-y-0.5">
                        <div>Current: <span className="font-mono font-semibold">${data.currentPrice.toFixed(2)}</span></div>
                        {data.smartEntry.priceAnchor != null && (
                          <div>Target entry: <span className="font-mono font-semibold">≤ ${data.smartEntry.priceAnchor.toFixed(2)}</span></div>
                        )}
                        {data.entry && (
                          <div className="text-[10px] text-[#94a3b8]">
                            Your entry: <span className="font-mono">${data.entry.price.toFixed(2)}</span> on {data.entry.date}
                            {' · '}
                            <span className={((data.currentPrice / data.entry.price - 1) * 100) >= 0 ? 'text-emerald-300' : 'text-red-300'}>
                              {fmtPct((data.currentPrice / data.entry.price - 1) * 100)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {data.smartEntry.reasons.length > 0 && (
                      <ul className="text-[11px] space-y-1 text-[#cbd5e1] leading-snug">
                        {data.smartEntry.reasons.map((r, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#64748b]">·</span>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Risks + Positives */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-red-400/30 bg-red-500/5 rounded p-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-red-300 mb-2">Risks</div>
                {data.summary.risks.length === 0 ? (
                  <div className="text-[11px] text-[#64748b] italic">No flagged risks</div>
                ) : (
                  <ul className="space-y-1 text-[11px] text-red-100/90">
                    {data.summary.risks.slice(0, 6).map((r, i) => (
                      <li key={i} className="leading-snug">{r}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border border-emerald-400/30 bg-emerald-500/5 rounded p-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300 mb-2">Positives</div>
                {data.summary.positives.length === 0 ? (
                  <div className="text-[11px] text-[#64748b] italic">No flagged positives</div>
                ) : (
                  <ul className="space-y-1 text-[11px] text-emerald-100/90">
                    {data.summary.positives.slice(0, 6).map((p, i) => (
                      <li key={i} className="leading-snug">{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Last earnings + Street consensus */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.lastEarnings && (
                <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8] mb-2">Last Earnings</div>
                  <div className="text-[12px] text-white">
                    <div>{data.lastEarnings.date} · {data.lastEarnings.period}</div>
                    <div className="mt-1">
                      EPS <span className="font-mono">{fmtMoney(data.lastEarnings.epsActual)}</span> vs <span className="font-mono">{fmtMoney(data.lastEarnings.epsEstimate)}</span> est
                      {' '}
                      <span className={`font-mono font-semibold ${(data.lastEarnings.surprisePct ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                        {fmtPct(data.lastEarnings.surprisePct)}
                      </span>
                    </div>
                    {data.lastEarnings.yoyGrowthPct != null && (
                      <div className="mt-1 text-[10px] text-[#94a3b8]">Y/Y growth: <span className="font-mono">{fmtPct(data.lastEarnings.yoyGrowthPct)}</span></div>
                    )}
                  </div>
                </div>
              )}
              {data.consensus && (
                <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8] mb-2">Street Consensus</div>
                  <div className="text-[12px] text-white">
                    <div>
                      Target <span className="font-mono font-semibold">${data.consensus.targetPrice?.toFixed(0)}</span>
                      {' '}
                      <span className="text-emerald-300 font-mono">
                        ({fmtPct(((data.consensus.targetPrice ?? 0) / data.currentPrice - 1) * 100, 0)} implied)
                      </span>
                    </div>
                    <div className="mt-1 text-[10px] text-[#94a3b8]">
                      Rating <span className="font-mono">{data.consensus.rating?.toFixed(1)}/5</span>
                      {' · '}
                      {data.consensus.buy ?? 0} buy / {data.consensus.hold ?? 0} hold / {data.consensus.sell ?? 0} sell
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Latest Earnings Call — summary + access to full transcript (Phase 8.5, 2026-05-22) */}
            {data.latestTranscript && (
              <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8]">
                    Latest Earnings Call
                    <span className="ml-2 font-mono text-[#64748b] normal-case font-normal">
                      {data.latestTranscript.callDate} · {data.latestTranscript.period}
                    </span>
                  </div>
                  <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${TONE_STYLE[data.latestTranscript.tone].pill}`}>
                    {TONE_STYLE[data.latestTranscript.tone].label}
                  </span>
                </div>

                <div className="text-[13px] text-white font-semibold leading-snug mb-1">
                  {data.latestTranscript.headline}
                </div>
                <div className="text-[11px] text-[#cbd5e1] leading-snug">
                  {data.latestTranscript.oneLiner}
                </div>

                <div className="mt-2 flex items-center gap-3 text-[10px]">
                  <button
                    type="button"
                    onClick={() => setTranscriptExpanded((v) => !v)}
                    className="text-[#3b82f6] hover:underline"
                  >
                    {transcriptExpanded ? 'Hide details ▲' : 'Show details ▼'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFullTranscriptOpen(true)}
                    className="text-[#3b82f6] hover:underline"
                  >
                    View full transcript ↗
                  </button>
                </div>

                {transcriptExpanded && (
                  <div className="mt-3 space-y-3 text-[11px] text-[#cbd5e1] leading-snug border-t border-[#1e293b] pt-3">
                    {data.latestTranscript.guidanceChanges && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-emerald-300/70 mb-1">Guidance changes</div>
                        <div>{data.latestTranscript.guidanceChanges}</div>
                      </div>
                    )}
                    {data.latestTranscript.capitalAllocation && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-[#94a3b8] mb-1">Capital allocation</div>
                        <div>{data.latestTranscript.capitalAllocation}</div>
                      </div>
                    )}
                    {data.latestTranscript.analystPushback && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-amber-300/70 mb-1">Analyst pushback</div>
                        <div>{data.latestTranscript.analystPushback}</div>
                      </div>
                    )}
                    {data.latestTranscript.risksFlagged && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-red-300/70 mb-1">Risks flagged</div>
                        <div>{data.latestTranscript.risksFlagged}</div>
                      </div>
                    )}
                    {data.latestTranscript.keyQuotes && data.latestTranscript.keyQuotes.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-[#94a3b8] mb-1">Key quotes</div>
                        <ul className="space-y-2">
                          {data.latestTranscript.keyQuotes.map((q, i) => (
                            <li key={i} className="border-l-2 border-[#1e293b] pl-2">
                              <div className="text-white italic">&ldquo;{q.quote}&rdquo;</div>
                              <div className="text-[#64748b] text-[10px] mt-0.5">
                                — {q.speaker}{q.context ? ` · ${q.context}` : ''}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Per-stock one-pager (left) + EPS revision chart (right). Panel
                forced to singleColumn so its card fills its parent column.
                Chart forced to chartOnly so it renders just the sparkline +
                explanation prose below. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
              <HoldingsResearchPanel symbols={[symbol]} title="Per-stock summary" singleColumn />
              <EpsRevisionChart symbols={[symbol]} chartOnly />
            </div>

            {/* Earnings momentum + composite velocity (2026-05-22 Steve directive —
                surface the per-stock signals so the drawer is the single place
                to look). Two paired blocks share one row to match the existing
                Last Earnings / Street Consensus rhythm. */}
            {(data.earningsMomentum || data.compositeVelocity) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.earningsMomentum && (
                  <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8]">Earnings Momentum</div>
                      <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${
                        data.earningsMomentum.signal === 'pos'  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40' :
                        data.earningsMomentum.signal === 'neg'  ? 'bg-red-500/15 text-red-300 border-red-400/40' :
                        data.earningsMomentum.signal === 'flat' ? 'bg-amber-500/15 text-amber-300 border-amber-400/40' :
                                                                  'bg-slate-500/15 text-slate-300 border-slate-400/40'
                      }`}>
                        {data.earningsMomentum.signal === 'pos'  ? '+VE' :
                         data.earningsMomentum.signal === 'neg'  ? '-VE' :
                         data.earningsMomentum.signal === 'flat' ? 'NEUTRAL' :
                                                                   'NO DATA'}
                      </span>
                    </div>
                    <div className="text-[12px] text-white space-y-1">
                      <div>
                        EPS revision z-score: <span className="font-mono tabular-nums">
                          {data.earningsMomentum.epsRevision != null ? (data.earningsMomentum.epsRevision >= 0 ? '+' : '') + data.earningsMomentum.epsRevision.toFixed(2) : '—'}
                        </span>
                      </div>
                      <div>
                        Last quarter surprise: <span className={`font-mono tabular-nums ${
                          (data.earningsMomentum.earningsSurprisePct ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-300'
                        }`}>
                          {data.earningsMomentum.earningsSurprisePct != null ? `${(data.earningsMomentum.earningsSurprisePct * 100).toFixed(1)}%` : '—'}
                        </span>
                      </div>
                      {/* Revision velocity family (2026-05-22). Direction
                          per-window + acceleration (Δ7 − Δ30). Sign-tinted;
                          null cells dim to em-dash. */}
                      {(data.earningsMomentum.revisionDelta7d != null
                        || data.earningsMomentum.revisionDelta30d != null
                        || data.earningsMomentum.revisionDelta90d != null) && (
                        <div className="grid grid-cols-4 gap-1 mt-2 pt-2 border-t border-[#1e293b]">
                          {[
                            { label: 'Δ 7d',  v: data.earningsMomentum.revisionDelta7d  },
                            { label: 'Δ 30d', v: data.earningsMomentum.revisionDelta30d },
                            { label: 'Δ 90d', v: data.earningsMomentum.revisionDelta90d },
                            { label: 'Accel', v: data.earningsMomentum.revisionAcceleration },
                          ].map(({ label, v }) => {
                            const tone = v == null ? 'text-[#64748b]'
                              : v > 0.001 ? 'text-emerald-300'
                              : v < -0.001 ? 'text-red-300'
                              : 'text-[#94a3b8]';
                            const txt = v == null ? '—'
                              : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
                            return (
                              <div key={label} className="text-center">
                                <div className="text-[8px] uppercase tracking-wider text-[#64748b]">{label}</div>
                                <div className={`text-[11px] font-mono tabular-nums ${tone}`}>{txt}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="text-[10px] text-[#64748b] mt-1">
                        Blend of analyst-revision direction (forward) and most-recent EPS surprise (lagging). Δ7/Δ30/Δ90 = % change in BBG consensus eps1y over rolling windows; Accel = Δ7 − Δ30 (recent vs monthly trend).
                      </div>
                    </div>
                  </div>
                )}
                {data.compositeVelocity && (
                  <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8]">Composite Velocity</div>
                      {data.compositeVelocity.d20Rank != null && (
                        <span className="text-[10px] font-mono text-[#cbd5e1]">
                          20d rank{' '}
                          <span className={
                            data.compositeVelocity.d20PctRank! >= 0.9 ? 'text-emerald-300' :
                            data.compositeVelocity.d20PctRank! <= 0.1 ? 'text-red-300' :
                            'text-[#cbd5e1]'
                          }>#{data.compositeVelocity.d20Rank}</span>
                          <span className="text-[#64748b]"> / {data.compositeVelocity.universeCount}</span>
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-white grid grid-cols-3 gap-2 text-center">
                      {(['d1', 'd5', 'd20'] as const).map((k) => {
                        const v = data.compositeVelocity![k];
                        return (
                          <div key={k}>
                            <div className="text-[9px] uppercase text-[#64748b]">Δ{k === 'd1' ? '1d' : k === 'd5' ? '5d' : '20d'}</div>
                            <div className={`font-mono tabular-nums text-[14px] ${
                              v == null ? 'text-[#475569]' :
                              v > 0.15 ? 'text-emerald-300' :
                              v < -0.15 ? 'text-red-300' :
                              'text-amber-300/80'
                            }`}>
                              {v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notable Congressional Activity (last 90 days) — surface only, 2026-05-22 */}
            {data.congressionalActivity && data.congressionalActivity.totalLast90Days > 0 && (
              <div className="border border-[#1e293b] bg-[#0f141c] rounded p-3">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8]">
                    Notable Congressional Activity
                    <span className="ml-2 font-mono text-[#64748b] normal-case font-normal">
                      last {data.congressionalActivity.windowDays} days
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-[#cbd5e1]">
                    <span className="text-emerald-300">{data.congressionalActivity.buyCount} buy</span>
                    <span className="text-[#64748b]"> · </span>
                    <span className="text-red-300">{data.congressionalActivity.sellCount} sell</span>
                  </span>
                </div>
                <ul className="space-y-1 text-[11px]">
                  {data.congressionalActivity.trades.slice(0, 5).map((t, i) => (
                    <li key={`${t.disclosureDate}-${t.legislator}-${i}`}
                        className="grid grid-cols-[72px_1fr_60px_120px_22px] gap-2 items-center">
                      <span className="text-[10px] text-[#64748b] font-mono tabular-nums">{t.transactionDate}</span>
                      <span className="text-white truncate">
                        {t.legislator}
                        <span className="text-[#64748b] text-[10px] ml-1">({t.chamber}-{t.district}{t.owner && t.owner !== 'Self' ? ` · ${t.owner}` : ''})</span>
                      </span>
                      <span className={`text-[10px] font-mono uppercase ${
                        /purchase/i.test(t.type) ? 'text-emerald-300' :
                        /sale|sold/i.test(t.type) ? 'text-red-300' :
                        'text-[#94a3b8]'
                      }`}>{t.type.toUpperCase()}</span>
                      <span className="text-[10px] text-[#94a3b8] font-mono truncate">{t.amount}</span>
                      <a href={t.link} target="_blank" rel="noopener" className="text-[#3b82f6] hover:underline text-right text-[10px]">↗</a>
                    </li>
                  ))}
                </ul>
                {data.congressionalActivity.trades.length > 5 && (
                  <div className="mt-2 text-[10px] text-[#64748b]">
                    +{data.congressionalActivity.trades.length - 5} more disclosed
                  </div>
                )}
              </div>
            )}

            {/* Event timeline */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94a3b8] mb-2">Recent events (last 30 days)</div>
              {data.events.length === 0 ? (
                <div className="text-[11px] text-[#64748b] italic">No recent events.</div>
              ) : (
                <ul className="space-y-1 border border-[#1e293b] bg-[#0f141c] rounded p-3">
                  {data.events.map((e, i) => (
                    <li key={`${e.ts}-${i}`} className="grid grid-cols-[12px_70px_1fr_50px] gap-2 items-center text-[11px]">
                      <span className={`inline-block w-2 h-2 rounded-full ${SENTIMENT_DOT[e.sentiment]}`} />
                      <span className="text-[10px] text-[#64748b] font-mono tabular-nums">{e.ts.slice(0, 10)}</span>
                      <span className="text-white truncate">
                        {e.sourceUrl ? (
                          <a href={e.sourceUrl} target="_blank" rel="noopener" className="hover:underline">{e.headline}</a>
                        ) : (
                          e.headline
                        )}
                      </span>
                      <span className="text-[9px] text-[#64748b] text-right">{relTime(e.ts)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-[9px] text-[#64748b] pt-2">
              Story compiled {new Date(data.asOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
              Sources: scores.json · target-prices.json · analyst-consensus.json · bbg-earnings.json · portfolio-events.jsonl{data.latestTranscript ? ' · transcripts/' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Full transcript modal — opens over the drawer, dims everything else.
          Esc closes it before the drawer-level Esc handler fires (see useEffect above). */}
      {fullTranscriptOpen && data?.latestTranscript && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-stretch justify-center p-6"
          onClick={() => setFullTranscriptOpen(false)}
        >
          <div
            className="bg-[#0a0e14] border border-[#1e293b] rounded-lg shadow-2xl max-w-4xl w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-[#0a0e14] border-b border-[#1e293b] px-5 py-3 flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-bold text-white">
                  {symbol} <span className="text-[#64748b] text-[12px] font-normal">
                    earnings call · {data.latestTranscript.callDate} · {data.latestTranscript.period}
                  </span>
                </h3>
                <div className="text-[10px] text-[#64748b] mt-0.5">
                  {(data.latestTranscript.fullText.length / 1000).toFixed(0)}K chars · source FMP
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFullTranscriptOpen(false)}
                className="text-[#94a3b8] hover:text-white px-2 py-1 text-[14px]"
                aria-label="Close transcript"
              >✕</button>
            </div>
            <div className="overflow-y-auto px-5 py-4">
              <pre className="text-[11px] text-[#cbd5e1] whitespace-pre-wrap font-mono leading-relaxed">
                {data.latestTranscript.fullText}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
